import { useMutation } from '@tanstack/react-query';
import { tokenAtom } from '@/atoms/auth';
import { jotaiStore } from '@/atoms/store';
import { baseUrl } from '@/constants';
import type { IFileExport } from '@/types';

/**
 * Export file as a mutation (imperative trigger, not a cached query).
 * Downloads the file via a blob URL and triggers browser download.
 */
export function useFetchExportFile() {
    return useMutation({
        mutationFn: async (params: IFileExport) => {
            const token = jotaiStore.get(tokenAtom);

            const searchParams = new URLSearchParams();
            if (params.format) searchParams.set('format', params.format);
            if (params.episode_id) searchParams.set('episode_id', String(params.episode_id));
            if (params.tags) searchParams.set('tags', params.tags);
            // Only the outline export sends this flag; leave it off for other export types.
            if (params.outlineOnly !== undefined) searchParams.set('outline-only', params.outlineOnly ? '1' : '0');
            const qs = searchParams.toString();

            const urlPath = `projects/${params.project_id}/export${params.type ? `/${params.type}` : ''}`;
            const url = `${baseUrl}${urlPath}${qs ? `?${qs}` : ''}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                cache: 'no-cache',
            });

            if (!response.ok) {
                throw new Error(`Export Error: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            let filename = '';
            const cd = response.headers.get('Content-Disposition');
            if (cd && cd.indexOf('attachment') !== -1) {
                const match = cd.match(/filename=(.+)/);
                if (match?.[1]) filename = match[1].replace(/['"]/g, '');
            }

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            link.click();

            return { url: blobUrl, filename };
        },
    });
}
